import React, { useEffect, useMemo } from 'react';
import styled from '@emotion/styled';
import {
  Accordion,
  // InputLabel,
  AccordionDetails,
  AccordionSummary,
  Chip,
  Divider,
  Typography,
  Fade
} from '@mui/material';
import PolarisIcon from '@/components/_common/PolarisIcon';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { selectPackagingActiveCutType } from '@/redux/selectors/packagingSelectors';
import { selectFileTypeGroupings } from '@/redux/selectors/constantsSelectors';
import { deepCopy, capitalizeNoun } from '@/_utils/utils';
// import Datepicker from '@/components/_common/Datepicker/Datepicker';
import { setFilters } from '@/redux/slices/orderPlacementSlice';
import { selectFilters } from '@/redux/selectors/orderPlacementSelectors';
import { DRAGGABLE_TYPES_ALLOWED_MEDIA } from '../constants';
import { useToast } from '@/components/_common/Toast';
import { useGetFilteredInventory } from '@/components/Packaging/InventoryAssignmentModal/useGetFilteredInventory.hook';

const accordionDetailsStyles: React.CSSProperties = {
  marginBottom: 20,
  paddingLeft: 0,
  paddingRight: 0
};

const FiltersSidebar = () => {
  const dispatch = useAppDispatch();
  const filters = useAppSelector(selectFilters);
  const fileTypeGroupings = useAppSelector(selectFileTypeGroupings);
  const activeCutType = useAppSelector(selectPackagingActiveCutType);
  const { error } = useToast();

  // TODO: Removed per PBS-738; Will re-add later when supported
  // const cutTypes = useAppSelector(selectPackagingOptions);
  // const sortedCutTypes = [...cutTypes].sort((a, b) => a.label.localeCompare(b.label));
  // const [showMoreTypes, setShowMoreTypes] = useState(false);

  const enabledFileGroupingFilters = useMemo(() => {
    const enabledFileGroupings = Object.keys(fileTypeGroupings).reduce((acc, key) => {
      const isEnabled = fileTypeGroupings[key as keyof typeof fileTypeGroupings].some((ext) => filters.file_type_filter?.includes(ext));
      if (isEnabled) {
        acc.push(key);
      }
      return acc;
    }, [] as string[]);
    return enabledFileGroupings;
  }, [filters]);
  const inventoryCount = useGetFilteredInventory()?.length;
  const allowedMediaTypes = ['video', 'audio', 'subtitle', 'image'];


  const updateFilters = (key: keyof (typeof filters)['cut_type_filter'], value: string) => {
    if (!activeCutType) {
      error('No active cut type selected');
      return;
    }

    const { video, audio, subtitle, image } = fileTypeGroupings;
    const allowedMediaTypes = DRAGGABLE_TYPES_ALLOWED_MEDIA[activeCutType.type];
    const newFilters = deepCopy(filters);

    if (key === 'type') {
      if ((newFilters.cut_type_filter || []).includes(value)) {
        newFilters.cut_type_filter = newFilters.cut_type_filter.filter((item: string) => item !== value);
      } else {
        newFilters.cut_type_filter.push(value);
      }
    } else if (key === 'media') {
      const typedValue = value as keyof typeof fileTypeGroupings;
      const grouping = fileTypeGroupings[typedValue] as string[];
      if ((newFilters.file_type_filter || []).some((ext: string) => grouping.includes(ext))) {
        // Remove all associated file extensions if already applied
        const filteredFilters = (newFilters.file_type_filter || []).filter((ext: string) => !grouping.includes(ext));
        newFilters.file_type_filter =
          filteredFilters.length > 0
            ? filteredFilters
            : [
                ...(allowedMediaTypes.includes('video') ? video : []),
                ...(allowedMediaTypes.includes('audio') ? audio : []),
                ...(allowedMediaTypes.includes('subtitle') ? subtitle : []),
                ...(allowedMediaTypes.includes('image') ? image : [])
              ];
      } else {
        // Add all associated file extensions
        newFilters.file_type_filter = [...new Set([...(newFilters.file_type_filter || []), ...grouping])];
      }
    } /*else if (key === 'date_start') {
      if (newFilters.date_added_start) {
        delete newFilters.date_added_start;
      } else {
        newFilters.date_added__gte = value;
      }
    } else if (key === 'date_end') {
      if (newFilters.date_added__lte) {
        delete newFilters.date_added__lte;
      } else {
        newFilters.date_added__lte = value;
      }
    }*/
    dispatch(setFilters(newFilters));
  };

  useEffect(() => {
    if (activeCutType && !(filters.cut_type_filter || []).includes(activeCutType.kind)) {
      console.log('setting enabled >>>', activeCutType.kind);
    }
  }, [activeCutType]);

  return (
    <Wrapper>
      <div style={{ position: 'sticky', top: 0, zIndex: 1, background: 'inherit' }}>
        <ColumnHeader>
          <PolarisIcon icon="filter_lines" height={24} />
          <Typography variant="h5" fontWeight={'bold'}>
            Filters
          </Typography>
        </ColumnHeader>
        <ResultsCount>
          {filters.version_name_filter?.length === 0 ? 'Choose a title to begin filtering files.' : `${inventoryCount} results`}
        </ResultsCount>
      </div>
      <ScrollableDiv>
        {/* <AccordionWrapper>
          <Accordion defaultExpanded>
            <AccordionSummary
              expandIcon={<PolarisIcon icon="down" height={15} />}
              aria-controls="panel1-content"
              id="panel1-header"
              sx={{ margin: 0 }}
            >
              <TitleAndFilterCount>
                <Typography variant={'h6'} fontWeight={'bold'}>
                  Type
                </Typography>
                <Fade in={!!filters?.cut_type_filter?.length} timeout={300}>
                  <Typography variant={'body2'} fontWeight={'bold'} color={'text.secondary'} sx={{ height: '100%', marginLeft: 'auto' }}>
                    {`${filters?.cut_type_filter?.length} selected`}
                  </Typography>
                </Fade>
              </TitleAndFilterCount>
            </AccordionSummary>
            <AccordionDetails style={accordionDetailsStyles}>
              <ChipWrapper>
                {(showMoreTypes ? sortedCutTypes : sortedCutTypes.slice(0, 7)).map((option) => (
                  <FilterChip
                    enabled={+(filters?.cut_type_filter || [])?.includes(option.kind)}
                    key={option.kind}
                    disabled={filters.version_name_filter?.length === 0}
                    label={option.label}
                    variant="outlined"
                    onClick={() => updateFilters('type', option.kind)}
                  />
                ))}
                <ShowMoreChip
                  label={!showMoreTypes ? 'Show More' : 'Show Less'}
                  onClick={handleShowMoreTypes}
                  icon={<PolarisIcon icon={!showMoreTypes ? 'add' : 'subtract'} height={15} />}
                />
              </ChipWrapper>
            </AccordionDetails>
          </Accordion>
        </AccordionWrapper> */}
        <Divider sx={{ width: '100%', margin: '8px 0' }} />
        <AccordionWrapper>
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<PolarisIcon icon="down" height={15} />} aria-controls="panel2-content" id="panel2-header">
              <TitleAndFilterCount>
                <Typography variant={'h6'} fontWeight={'bold'}>
                  Media
                </Typography>
                <Fade in={filters.version_name_filter?.length > 0 && enabledFileGroupingFilters.length > 0} timeout={300}>
                  <Typography variant={'body2'} fontWeight={'bold'} color={'text.secondary'} sx={{ height: '100%', marginLeft: 'auto' }}>
                    {`${enabledFileGroupingFilters.length} selected`}
                  </Typography>
                </Fade>
              </TitleAndFilterCount>
            </AccordionSummary>
            <AccordionDetails style={accordionDetailsStyles}>
              <ChipWrapper>
                {allowedMediaTypes.map((key) => (
                  <FilterChip
                    enabled={filters.version_name_filter?.length === 0 ? +false : +enabledFileGroupingFilters.includes(key)}
                    key={key}
                    label={capitalizeNoun(key)}
                    variant="outlined"
                    onClick={() => updateFilters('media', key)}
                  />
                ))}
              </ChipWrapper>
            </AccordionDetails>
          </Accordion>
        </AccordionWrapper>

        {/*
        <Divider sx={{ width: '100%', margin: '8px 0' }} />
        <AccordionWrapper>
          <Accordion>
            <AccordionSummary expandIcon={<PolarisIcon icon="down" height={15} />} aria-controls="panel3-content" id="panel3-header">
              <Typography variant={'h6'} fontWeight={'bold'} style={{ fontSize: '1.2rem' }}>
                Date Added
              </Typography>
            </AccordionSummary>
            <AccordionDetails style={{ marginBottom: 20 }}>
              <DatepickerContainer>
                <DateWithClear>
                  <InputLabel sx={{ mb: 1 }}>Start Date</InputLabel>
                  <div style={{ position: 'relative' }}>
                    <StyledDatepicker
                      disabled={filters.version_name_filter?.length === 0}
                      selected={filters?.date_added__gte ? new Date(filters?.date_added__gte) : undefined}
                      onChange={(date) => updateFilters('date_start', date ? date.toISOString() : '')}
                    />
                    {filters.version_name_filter?.length > 0 && (
                      // TODO: ClearDateChip overlaps calendar icon
                      <ClearDateChip
                        label="Clear"
                        onClick={() => updateFilters('date_start', '')}
                        icon={<PolarisIcon icon="close" height={15} />}
                        variant="outlined"
                      />
                    )}
                  </div>
                </DateWithClear>
                <DateWithClear>
                  <InputLabel sx={{ mb: 1 }}>End Date</InputLabel>
                  <div style={{ position: 'relative' }}>
                    <StyledDatepicker
                      disabled={filters.version_name_filter?.length === 0}
                      selected={filters?.date_added__lte ? new Date(filters?.date_added__lte) : undefined}
                      onChange={(date) => updateFilters('date_end', date ? date.toISOString() : '')}
                    />
                    {filters.version_name_filter?.length > 0 && (
                      // TODO: ClearDateChip overlaps calendar icon
                      <ClearDateChip
                        label="Clear"
                        onClick={() => updateFilters('date_end', '')}
                        icon={<PolarisIcon icon="close" height={15} />}
                        variant="outlined"
                      />
                    )}
                  </div>
                </DateWithClear>
              </DatepickerContainer>
            </AccordionDetails>
          </Accordion>
        </AccordionWrapper>
        */}
      </ScrollableDiv>
    </Wrapper>
  );
};

export default FiltersSidebar;

const Wrapper = styled.div`
  width: 318px;
  min-width: 318px;
  background: ${({ theme }) => theme.palette.background.paper};
  padding: ${({ theme }) => theme.spacing(3)};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
`;

const ScrollableDiv = styled.div`
  overflow-y: scroll;
  overflow-x: hidden;
  flex-grow: 1;
  width: 100%;
  display: block;
`;

const AccordionWrapper = styled.div`
  margin: ${({ theme }) => theme.spacing(1, 0)};

  .MuiAccordion-root {
    border: none;
  }

  .MuiAccordionSummary-root {
    flex-direction: row;
    min-height: unset;
  }
`;

const ColumnHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  column-gap: ${({ theme }) => theme.spacing(2)};
`;

const ResultsCount = styled.div`
  padding: ${({ theme }) => theme.spacing(1, 0)};
  font-size: 1.2rem;
  font-weight: 200;
  opacity: 0.4;
`;

const FilterChip = styled(Chip)<{ enabled: number }>`
  background-color: ${({ theme, enabled }) => (enabled ? theme.palette.primary.main : 'transparent')};
  border: 1px solid ${({ theme, enabled }) => (enabled ? theme.palette.primary.main : theme.palette.grey[500])};

  &.MuiChip-clickable:hover {
    background-color: transparent;
    color: ${({ theme }) => theme.palette.primary.main};
    border: 1px solid ${({ theme }) => theme.palette.primary.main};
  }
`;

const ChipWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  column-gap: ${({ theme }) => theme.spacing(0.8)};
  row-gap: ${({ theme }) => theme.spacing(1)};
`;

// const ShowMoreChip = styled(Chip)`
//   background: transparent;
//   color: ${({ theme }) => theme.palette.secondary.main};
//   border: none;
//   cursor: pointer;

//   .MuiChip-icon {
//     color: ${({ theme }) => theme.palette.secondary.main};
//   }

//   &:hover {
//     background: transparent;
//     color: ${({ theme }) => theme.palette.primary.main};

//     .MuiChip-icon {
//       color: ${({ theme }) => theme.palette.primary.main};
//     }
//   }
// `;

/*
const DatepickerContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: ${({ theme }) => theme.spacing(2)};
  width: 100%;
`;

const StyledDatepicker = styled(Datepicker)`
  width: 240px;
`;

const DateWithClear = styled.div`
  position: relative;
  .MuiChip-root {
    display: none;
  }

  &:hover {
    .MuiChip-root {
      display: flex;
    }
  }
`;

const ClearDateChip = styled(Chip)`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: ${({ theme }) => theme.spacing(1)};
`;
*/

const TitleAndFilterCount = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  margin-right: ${({ theme }) => theme.spacing(2)};
`;
